import type {
  AnyAppliedQuery,
  PhenotypeV3Selection,
  PreviewQueryInput,
  PreviewQueryTag,
  TablesQuery
} from 'modules/cohort-browser/components/query/preview-query/types'
import { AppliedQueryType } from 'modules/cohort-browser/enums'
import { CbQueryTagTypes, FilterApiQueryOperators } from 'modules/cohort-browser/enums'
import type { DataAccessRequestPhenotypicQuery } from 'services/results/types'

const normalizeQueryViewTag = (tag: Record<string, unknown>): PreviewQueryTag | null => {
  if (tag.tagType === CbQueryTagTypes.Phenotypic) {
    return {
      tagType: CbQueryTagTypes.Phenotypic,
      fieldId: typeof tag.fieldId === 'number' ? tag.fieldId : undefined,
      fieldName: typeof tag.fieldName === 'string' ? tag.fieldName : undefined,
      value: typeof tag.value === 'string' ? tag.value : undefined,
      instance: Array.isArray(tag.instance) ? (tag.instance as string[]) : undefined,
      selection: tag.selection as PhenotypeV3Selection | undefined
    }
  }

  if (tag.tagType === CbQueryTagTypes.Aggregation) {
    return {
      tagType: CbQueryTagTypes.Aggregation,
      aggregationOperation: tag.aggregationOperation as FilterApiQueryOperators | undefined
    }
  }

  if (tag.tagType === CbQueryTagTypes.Bracket) {
    return {
      tagType: CbQueryTagTypes.Bracket,
      isOpening: tag.isOpening === true
    }
  }

  if (
    tag.fieldId !== undefined ||
    tag.fieldName !== undefined ||
    tag.value !== undefined ||
    tag.selection !== undefined
  ) {
    return {
      tagType: CbQueryTagTypes.Phenotypic,
      fieldId: typeof tag.fieldId === 'number' ? tag.fieldId : undefined,
      fieldName: typeof tag.fieldName === 'string' ? tag.fieldName : undefined,
      value: typeof tag.value === 'string' ? tag.value : undefined,
      selection: tag.selection as PhenotypeV3Selection | undefined
    }
  }

  return null
}

const normalizeQueryView = (queryView: Record<string, unknown>[]): PreviewQueryTag[] =>
  queryView.flatMap(tag => {
    const normalizedTag = normalizeQueryViewTag(tag)

    return normalizedTag ? [normalizedTag] : []
  })

const PREVIEWABLE_MODES = new Set<AppliedQueryType>([
  AppliedQueryType.Standard,
  AppliedQueryType.Sql,
  AppliedQueryType.Tables
])

const SQL_MODES = new Set<AppliedQueryType>([
  AppliedQueryType.Sql,
  AppliedQueryType.OmicsSql,
  AppliedQueryType.ClinicalSql,
  AppliedQueryType.DatahubSql,
  AppliedQueryType.DatahubAiSql
])

export const mapDataAccessRequestPhenotypicQueryToPreviewQuery = (
  phenotypic: DataAccessRequestPhenotypicQuery
): PreviewQueryInput | null => {
  const rawMode = phenotypic.participantsAppliedType?.trim()

  if (rawMode === undefined || rawMode.length === 0) {
    return null
  }

  const mode = rawMode as AppliedQueryType

  if (mode === AppliedQueryType.Reset || mode === AppliedQueryType.DatahubReset) {
    return null
  }

  if (mode === AppliedQueryType.Standard) {
    const queryView = phenotypic.queryView

    if (queryView === undefined || queryView.length === 0) {
      return null
    }

    const normalizedQueryView = normalizeQueryView(queryView)

    if (normalizedQueryView.length === 0) {
      return null
    }

    return { mode, query: normalizedQueryView }
  }

  if (SQL_MODES.has(mode)) {
    const sqlQuery = phenotypic.sqlQuery?.trim()

    if (sqlQuery === undefined || sqlQuery.length === 0) {
      return null
    }

    return { mode: AppliedQueryType.Sql, query: sqlQuery }
  }

  if (mode === AppliedQueryType.Tables) {
    const tablesQuery = phenotypic.query as TablesQuery | undefined

    if (tablesQuery?.tables === undefined || tablesQuery.tables.length === 0) {
      return null
    }

    return { mode: AppliedQueryType.Tables, query: tablesQuery }
  }

  if (!PREVIEWABLE_MODES.has(mode)) {
    return null
  }

  return null
}

export const hasViewablePhenotypicPreviewQuery = (phenotypic?: DataAccessRequestPhenotypicQuery): boolean =>
  phenotypic !== undefined && mapDataAccessRequestPhenotypicQueryToPreviewQuery(phenotypic) !== null

export type { AnyAppliedQuery, PreviewQueryInput }
